import { api } from './client';
import type { BulkItem } from '@/lib/bulk-selection';
import type { Message } from '@/types';

export interface MessageListResponse {
  messages: Message[];
  pagination: { page: number; limit: number; total: number };
}

export interface SendMessageInput {
  /** Reply/forward only: send as this mailbox instead of the original's (B-08). */
  fromMailboxId?: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  text?: string;
  html?: string;
  attachments?: { filename: string; contentType?: string; content?: string }[];
  /** Forward only: quote the conversation and optionally include its files. */
  thread?: boolean;
  includeOriginalAttachments?: boolean;
}

export interface ThreadMember {
  uid: number;
  folder: string;
  fromEmail: string | null;
  fromName: string | null;
  subject: string | null;
  date: number | null;
  bodyText: string | null;
  hasAttachments: boolean;
}

/** The conversation a message belongs to: oldest first, at most `max` of `total` (backend mail/thread-members.js). */
export interface ThreadResponse { total: number; max: number; members: ThreadMember[] }

export async function listMessages(mailboxId: string, params: { folder?: string; page?: number; limit?: number; unread?: boolean; action?: 'clean' | 'review' | 'spam'; search?: string } = {}): Promise<MessageListResponse> {
  const qs = new URLSearchParams();
  if (params.folder) qs.set('folder', params.folder);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.unread !== undefined) qs.set('unread', String(params.unread));
  if (params.action) qs.set('action', params.action);
  if (params.search) qs.set('search', params.search);
  return api.get<MessageListResponse>(`/mailboxes/${mailboxId}/messages?${qs.toString()}`);
}

// UIDs are only unique per IMAP folder; a Junk message can share a UID with an
// INBOX one. Single-uid calls pass the folder the row was listed in as ?folder=.
export function folderQs(folder?: string | null): string {
  return folder ? `?${new URLSearchParams({ folder }).toString()}` : '';
}

export async function getMessage(mailboxId: string, uid: number, folder?: string | null): Promise<{ message: Message }> {
  return api.get<{ message: Message }>(`/mailboxes/${mailboxId}/messages/${uid}${folderQs(folder)}`);
}

export async function getThread(mailboxId: string, uid: number, folder?: string | null): Promise<ThreadResponse> {
  return api.get<ThreadResponse>(`/mailboxes/${mailboxId}/messages/${uid}/thread${folderQs(folder)}`);
}

export async function sendMessage(mailboxId: string, input: SendMessageInput): Promise<{ ok: boolean; messageId: string }> {
  return api.post<{ ok: boolean; messageId: string }>(`/mailboxes/${mailboxId}/messages`, input);
}

export async function replyMessage(mailboxId: string, uid: number, input: Partial<SendMessageInput>, folder?: string | null): Promise<{ ok: boolean; messageId: string }> {
  return api.post<{ ok: boolean; messageId: string }>(`/mailboxes/${mailboxId}/messages/${uid}/reply${folderQs(folder)}`, input);
}

export async function forwardMessage(mailboxId: string, uid: number, input: SendMessageInput, folder?: string | null): Promise<{ ok: boolean; messageId: string }> {
  return api.post<{ ok: boolean; messageId: string }>(`/mailboxes/${mailboxId}/messages/${uid}/forward${folderQs(folder)}`, input);
}

export async function markMessage(mailboxId: string, uid: number, updates: { isRead?: boolean; isFlagged?: boolean }, folder?: string | null): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(`/mailboxes/${mailboxId}/messages/${uid}${folderQs(folder)}`, updates);
}

export async function deleteMessage(mailboxId: string, uid: number, folder?: string | null): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>(`/mailboxes/${mailboxId}/messages/${uid}${folderQs(folder)}`);
}

// Bulk calls name each message by {uid, folder}: a uid alone is ambiguous between INBOX and Junk (B-03).
export async function bulkDeleteMessages(mailboxId: string, items: BulkItem[]): Promise<{ ok: boolean; deleted: number; failed: number }> {
  return api.post<{ ok: boolean; deleted: number; failed: number }>(`/mailboxes/${mailboxId}/messages/bulk-delete`, { items });
}

export async function bulkMarkRead(mailboxId: string, items: BulkItem[], isRead = true): Promise<{ ok: boolean; updated: number }> {
  return api.post<{ ok: boolean; updated: number }>(`/mailboxes/${mailboxId}/messages/bulk-read`, { items, isRead });
}

export async function bulkMarkSpam(mailboxId: string, items: BulkItem[], isSpam: boolean): Promise<{ ok: boolean; updated: number; moved?: number }> {
  return api.post<{ ok: boolean; updated: number; moved?: number }>(`/mailboxes/${mailboxId}/messages/bulk-spam`, { items, isSpam });
}

export async function markSpam(mailboxId: string, uid: number, isSpam: boolean, folder?: string | null): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/mailboxes/${mailboxId}/messages/${uid}/spam${folderQs(folder)}`, { isSpam });
}

export interface UnsubscribeResult {
  ok: boolean;
  error?: string;
  links?: { type: string; url: string; method?: string; source: string }[];
  followed?: { type: string; url: string; method?: string; source: string };
  result?: {
    type: string;
    url?: string;
    status?: number;
    finalUrl?: string;
    ok: boolean;
    needsBrowser?: boolean;
    manualFollowUrl?: string;
    browserTaskId?: string;
    error?: string;
    message?: string;
    browserEscalation?: { ok: boolean; error?: string; result?: unknown };
  };
}

export async function unsubscribe(mailboxId: string, uid: number, folder?: string | null): Promise<UnsubscribeResult> {
  return api.post<UnsubscribeResult>(`/mailboxes/${mailboxId}/messages/${uid}/unsubscribe${folderQs(folder)}`, {});
}

export interface UnsubscribeCandidate {
  uid: number;
  folder?: string;
  fromEmail: string;
  fromName: string;
  subject: string;
  snippet: string;
  date: number;
  linkCount: number;
  linkType: string;
}

export async function getUnsubscribeCandidates(mailboxId: string, folder?: string, spamAction?: 'clean' | 'review' | 'spam'): Promise<{ candidates: UnsubscribeCandidate[]; truncated?: boolean }> {
  const qs = new URLSearchParams();
  if (folder) qs.set('folder', folder);
  if (spamAction) qs.set('spamAction', spamAction);
  return api.get<{ candidates: UnsubscribeCandidate[] }>(`/mailboxes/${mailboxId}/unsubscribe/candidates${qs.toString() ? `?${qs.toString()}` : ''}`);
}

export async function checkOpenClawAccess(): Promise<{ gateway: boolean; error?: string }> {
  return api.get<{ gateway: boolean; error?: string }>(`/unsubscribe/check-openclaw`);
}

export interface BrowserTaskStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  result?: { success: boolean; message: string };
  error?: string;
  url?: string;
}

export async function getBrowserTaskStatus(taskId: string): Promise<BrowserTaskStatus> {
  return api.get<BrowserTaskStatus>(`/unsubscribe/browser-status/${taskId}`);
}

export function cancelBrowserTask(taskId: string): Promise<{ ok: boolean; status?: string }> {
  return api.post(`/unsubscribe/browser-cancel/${taskId}`, {});
}
